/**
 * Экран создания видео §65.
 *
 * Главный элемент — Prompt Composer. Advanced-параметры §15 свёрнуты
 * до необходимости (§85): каждый — компактный переключатель, а не секция формы.
 */
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Paperclip } from 'lucide-react';
import { AspectRatio } from '@alterium/shared';
import { Alert } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { PageHeader } from '../components/ui/PageHeader';
import { projectsApi } from '../api/projects';
import { looksApi } from '../api/looks';
import { useTranslation } from 'react-i18next';
import { errorText } from '../i18n/errors';

/** Длительность: `null` — «Auto», остальное подписывается числом секунд. */
const durations: Array<{ value: number | null }> = [
  { value: null },
  { value: 15 },
  { value: 30 },
  { value: 60 },
];

const ratios = [AspectRatio.R9X16, AspectRatio.R16X9, AspectRatio.R1X1];

export function CreateVideoPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation() as { state?: { prompt?: string } };

  const [prompt, setPrompt] = useState(location.state?.prompt ?? '');
  const [aspectRatio, setAspectRatio] = useState<string>(AspectRatio.R9X16);
  const [durationSec, setDurationSec] = useState<number | null>(null);
  const [lookId, setLookId] = useState<string | null>(null);
  const [captionsEnabled, setCaptionsEnabled] = useState(true);
  const [advanced, setAdvanced] = useState(false);

  const looks = useQuery({ queryKey: ['looks'], queryFn: looksApi.list });

  const create = useMutation({
    mutationFn: () =>
      projectsApi.create({ prompt: prompt.trim(), aspectRatio, durationSec, lookId, captionsEnabled }),
    onSuccess: (project) => navigate(`/projects/${project.id}`),
  });

  const primaryLook = (looks.data ?? []).find((look) => look.isPrimary);

  return (
    <div className="mx-auto max-w-[820px]">
      <PageHeader title={t('projects.createVideo')} description={t('create.subtitle')} />

      {create.isError ? (
        <div className="mb-6">
          <Alert tone="error">{errorText(create.error)}</Alert>
        </div>
      ) : null}

      <Card>
        <CardBody className="flex flex-col gap-5">
          <textarea
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            rows={7}
            autoFocus
            placeholder={t('create.promptPlaceholder')}
            className="w-full resize-none bg-transparent text-body leading-relaxed text-content placeholder:text-content-muted focus:outline-none"
          />

          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="tertiary" disabled>
              <Paperclip className="h-4 w-4" strokeWidth={1.75} />{t('create.materials')}</Button>

            <Chip active onClick={() => setAdvanced((open) => !open)}>
              {aspectRatio}
            </Chip>
            <Chip active onClick={() => setAdvanced((open) => !open)}>
              {durationSec ? t('create.approxSeconds', { value: durationSec }) : t('create.auto')}
            </Chip>
            <Chip active onClick={() => setAdvanced((open) => !open)}>
              {t('create.lookValue', {
                value: lookId
                  ? ((looks.data ?? []).find((l) => l.id === lookId)?.name ?? t('create.auto'))
                  : t('create.auto'),
              })}
            </Chip>
            <Chip active onClick={() => setCaptionsEnabled((value) => !value)}>
              {t('create.captionsValue', {
                value: captionsEnabled ? t('common.yes') : t('common.no'),
              })}
            </Chip>

            <Button
              variant="primary"
              className="ml-auto"
              onClick={() => create.mutate()}
              disabled={create.isPending || prompt.trim().length < 10}
            >
              {create.isPending ? t('create.pending') : t('create.submit')}
            </Button>
          </div>

          {/* §85: настройки появляются только тогда, когда они нужны */}
          {advanced ? (
            <div className="flex flex-col gap-4 border-t border-line pt-5">
              <Row label={t('create.format')}>
                {ratios.map((value) => (
                  <Chip key={value} active={aspectRatio === value} onClick={() => setAspectRatio(value)}>
                    {value}
                  </Chip>
                ))}
              </Row>

              <Row label={t('create.duration')}>
                {durations.map((item) => (
                  <Chip
                    key={item.value ?? 'auto'}
                    active={durationSec === item.value}
                    onClick={() => setDurationSec(item.value)}
                  >
                    {item.value === null
                      ? t('create.auto')
                      : t('create.seconds', { value: item.value })}
                  </Chip>
                ))}
              </Row>

              <Row label={t('create.look')}>
                <Chip active={lookId === null} onClick={() => setLookId(null)}>
                  Auto{primaryLook ? ` (${primaryLook.name})` : ''}
                </Chip>
                {(looks.data ?? []).map((look) => (
                  <Chip key={look.id} active={lookId === look.id} onClick={() => setLookId(look.id)}>
                    {look.name}
                  </Chip>
                ))}
              </Row>
            </div>
          ) : null}
        </CardBody>
      </Card>

      <p className="mt-4 text-meta text-content-muted">{t('create.costNotice')}</p>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="w-28 shrink-0 text-meta text-content-secondary">{label}</span>
      {children}
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? 'rounded-control border border-line-strong bg-surface-hover px-3 py-1.5 text-meta text-content'
          : 'rounded-control border border-line px-3 py-1.5 text-meta text-content-secondary transition-colors duration-fast hover:border-line-strong hover:text-content'
      }
    >
      {children}
    </button>
  );
}
